import React, { Fragment, useState, useEffect } from "react";
import { useSelector } from "react-redux";

import "./RequestDetailsModal.scss";
import { Container, Row, Col } from "../../../../components/Grid/Grid";
import Modal from "../../../../components/__common/Modal/Modal";
import { Svg, SVG_ICONS } from "../../../../components/__common/Svg/Svg";
import { Typography } from "../../../../components/__common/Typography/Typography";
import { Button } from "../../../../components/__common/_controls";
import { Image } from "../../../../components/__common/Image";
import { RequestStatus, RequestType } from "../../../../types/mydata";
import Alert from "../../../../components/__common/Alert/Alert";
import { TitleSection } from "./TitleSection";
import { Steps } from "./Steps";
import { useMyDataHooks } from "../../../../hooks/mydata-hooks";
import { ApplicationState } from "../../../../store";
import { MyDataState } from "../../../../store/reducers/mydata-reducer";
import { RequestDetailsEntity } from "../../../../types/request-details";
import { UtilsDateTime } from "../../../../utils/UtilsDateTIme";
import { UtilsConverter } from "../../../../utils/UtilsConverter";
import { UserState } from "../../../../store/reducers/user-reducer";
import Loading from "../../../../assets/images/gif/sdap-loading.json";
import { REQUEST_TYPE } from "../../../../types/user";
import { REQUEST_STATUS } from "../../../../constants/status";
import { useTranslation } from "react-i18next";
import { Cookie } from "../../../../services/storage";
import {
  REQUEST_NUMBER,
  SHOW_SURVEY,
  USER_SURVEY_SKIP,
} from "../../../../constants/storage";
import {
  EVENT_ACTION_DETAILS_DOWNLOAD_FILE,
  EVENT_CATEGORY_REQUEST_DETAILS,
} from "../../../../constants/analytics";
import { UtilsKeyboardAccessibility } from "../../../../utils/UtilsKeyboardAccessibility";
import FocusTrapping from "../../../../components/__hoc/FocusTrapping";
import GoogleAnalytics from "../../../../utils/GoogleAnalytics";
import Lottie from "lottie-react";
import { LayoutState } from "../../../../store/reducers/layout-reducer";

interface Props {
  show: boolean;
  requestNumber: string;
  onHide?: () => void;
  headerTestid?: string;
}

export const RequestDetailsModal: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { loadRequestDetails, sendFilePassword, closeFilePasswordModal } =
    useMyDataHooks();

  const myDataState = useSelector<ApplicationState, MyDataState>(
    (state) => state.myData
  );

  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const userState = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const { userData } = userState;
  const { languageTrans, isLanguageReady } = layoutState;
  const {
    details,
    isRequestDetailsLoading,
    isFilePasswordSent,
    isUserEmailNotSet,
  } = myDataState;

  const [status, setStatus] = useState<RequestStatus>(
    RequestStatus.REQUESTING || RequestStatus.QUEUED
  );
  const [requestType, setRequestType] = useState<RequestType>(
    RequestType.DOWNLOAD
  );
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isCancelAlertOpen, setCancelAlertOpen] = useState<boolean>(false);
  const [isSentFilePasswordOpen, setSentFilePasswordOpen] =
    useState<boolean>(false);

  const { show = false, requestNumber, onHide, headerTestid } = props;

  const modalTitle =
    requestType === RequestType.DOWNLOAD
      ? "PRIVACY_REQUEST_DETAILS_TITLE_DOWNLOAD"
      : requestType === RequestType.DELETE
        ? "PRIVACY_REQUEST_DETAILS_TITLE_DELETE"
        : "PRIVACY_REQUEST_DETAILS_TITLE_OPTOUT";

  const handleOnClickHideModal = () => {
    if (onHide) {
      handleSurveyCookie();
      onHide();
    }
    closeFilePasswordModal();
  };

  const handleSurveyCookie = () => {
    const requestComplete = details.request_status === REQUEST_STATUS.COMPLETED;
    const skippedUser = Cookie.get(USER_SURVEY_SKIP) || "[]";
    const skippedUserArr = JSON.parse(skippedUser);
    const userLogin = userState.userData.loginId;
    const userHasSkipped =
      skippedUserArr.findIndex((item: string) => userLogin === item) > -1;

    if (
      !userHasSkipped &&
      myDataState.details.display_survey &&
      requestComplete
    ) {
      Cookie.set(SHOW_SURVEY, "true", { expires: 186 });
    }
  };

  const handleOnClickCancel = () => {
    setModalOpen(false);
    setCancelAlertOpen(true);
  };

  const handleOnClickTransferPassword = async () => {
    if (userData.mail) {
      await sendFilePassword(details.request_number);
    }
    setSentFilePasswordOpen(true);
    setModalOpen(false);
  };

  useEffect(() => {
    setModalOpen(show);
  }, [show]);

  const handleOnHideAlert = () => {
    setSentFilePasswordOpen(false);
    setModalOpen(true);
  };

  useEffect(
    () => {
      if (
        requestNumber &&
        !userState.isGetUserDataLoading &&
        isLanguageReady &&
        languageTrans !== ""
      ) {
        loadRequestDetails(requestNumber);
        Cookie.remove(REQUEST_NUMBER);
      }
    },
    // eslint-disable-next-line
    [
      requestNumber,
      userState.isGetUserDataLoading,
      languageTrans,
      isLanguageReady,
    ]
  );

  useEffect(() => {
    if (details && details.request_number !== "") {
      setStatus(details.request_status as RequestStatus);
      setRequestType(details.request_type as RequestType);
    }
  }, [details]);

  useEffect(() => {
    if (isModalOpen) {
      UtilsKeyboardAccessibility.setFocus("request-details-modal");
    }
    if (isCancelAlertOpen) {
      UtilsKeyboardAccessibility.setFocus("request-details-alert");
    }
    if (isSentFilePasswordOpen) {
      UtilsKeyboardAccessibility.setFocus("send-password-alert");
    }
  }, [isModalOpen, isCancelAlertOpen, isSentFilePasswordOpen]);

  useEffect(() => {
    if (isRequestDetailsLoading) {
      return;
    }

    UtilsKeyboardAccessibility.addTabEventListener(
      "close-details-modal",
      "request-details-modal"
    );
    UtilsKeyboardAccessibility.addTabEventListener(
      "alert-cancel",
      "request-details-alert"
    );
    UtilsKeyboardAccessibility.addTabEventListener(
      "send-password-close",
      "send-password-alert"
    );

    return () => {
      UtilsKeyboardAccessibility.removeTabEventListener(
        "close-details-modal",
        "request-details-modal"
      );
      UtilsKeyboardAccessibility.removeTabEventListener(
        "alert-cancel",
        "request-details-alert"
      );
      UtilsKeyboardAccessibility.removeTabEventListener(
        "send-password-close",
        "send-password-alert"
      );
    };
  }, [isRequestDetailsLoading]);

  useEffect(() => {
    if (myDataState.serviceRequestToastError.isError) {
      setModalOpen(false);
    }
  }, [myDataState.serviceRequestToastError.isError]);
  return (
    <>
      {/* File Password Modal */}
      <FocusTrapping
        onHandleClose={() => handleOnHideAlert()}
        isActive={isSentFilePasswordOpen}
        modalId="send-password-alert"
      >
        <Alert
          wrapperClassname="request-details-alert"
          show={isSentFilePasswordOpen}
          onHideTestId="file-password-on-hide-button"
          onHide={() => {
            // closeFilePasswordModal();
            handleOnHideAlert();
          }}
          title={t("PRIVACY_REQUEST_SENDPASSWORD_MODAL_TITLE")}
          id="send-password-alert"
        >
          {(userData.mail || !isUserEmailNotSet) && (
            <>
              <Alert.Text className="request-details-alert__pwd-text">
                {!isFilePasswordSent
                  ? t("PRIVACY_REQUEST_SENDPASSWORD_MODAL_ERROR_TITLE")
                  : t("PRIVACY_REQUEST_SENDPASSWORD_MODAL_TEXT")}
              </Alert.Text>
              <Alert.Text className="request-details-alert__pwd-email">
                {userData.mail}
              </Alert.Text>
            </>
          )}
          <Alert.SubText
            className={
              !userData.mail || isUserEmailNotSet
                ? "request-details-alert__pwd-subtext"
                : undefined
            }
          >
            {userData.mail || !isUserEmailNotSet ? (
              !isFilePasswordSent ? (
                <span id="error-status">
                  {t("PRIVACY_REQUEST_SENDPASSWORD_MODAL_ERROR")}
                </span>
              ) : (
                t("PRIVACY_REQUEST_SENDPASSWORD_MODAL_TEXT2")
              )
            ) : (
              t("PRIVACY_REQUEST_DETAILS_TEXT_NO_EMAIL")
            )}
          </Alert.SubText>
          <Alert.Footer>
            <div className="button-container">
              <Button
                testId="close-filepassword-modal"
                type="primary"
                title={t("PRIVACY_CANCEL_REQUEST_BTN_CLOSE")}
                onClick={() => {
                  // closeFilePasswordModal();
                  handleOnHideAlert();
                }}
                id="send-password-close"
              ></Button>
            </div>
          </Alert.Footer>
        </Alert>
      </FocusTrapping>
      {/* Request Details Modal */}
      <FocusTrapping
        onHandleClose={handleOnClickHideModal}
        isActive={isModalOpen}
        modalId="request-detail-modal"
        testId="request-detail-modal-focus-trapping"
      >
        <Modal
          id="request-detail-modal"
          show={isModalOpen}
          className="request-detail-modal"
          wrapperClassName="request-details"
          testId="request-detail-modal"
        >
          {isRequestDetailsLoading && (
            <Lottie
              className="request-details__loading"
              animationData={Loading}
              loop={true}
              style={{ width: 60, height: 60 }}
            />
          )}

          {!isRequestDetailsLoading && (
            <>
              <Modal.Header
                headertestid={headerTestid}
                onHide={handleOnClickHideModal}
              >
                <Modal.Title ttsButtonId="request-details-tts">
                  {t(modalTitle)}
                </Modal.Title>
              </Modal.Header>

              {details && details.request_number !== "" && (
                <Modal.Body>
                  <div className="request-details__headings">
                    <Steps status={status} />
                    <TitleSection
                      details={details}
                      status={status}
                      requestType={requestType}
                      onClickCancel={() => handleOnClickCancel()}
                      onClickTransferPassword={() =>
                        handleOnClickTransferPassword()
                      }
                    />
                  </div>
                  <div className="request-details__services-list">
                    {details.request_type === RequestType.DOWNLOAD &&
                    details.request_status === RequestStatus.COMPLETED ? (
                      <DownloadCompletedServices details={details} />
                    ) : (
                      <Container>
                        <Container>
                          <Row>
                            {details.request_status ===
                              RequestStatus.PROCESSING ||
                            details.request_status ===
                              RequestStatus.COMPLETED ||
                            details.request_status === RequestStatus.DENIED ? (
                              <>
                                <Col md={6}>
                                  <Typography
                                    variant="body2"
                                    className="title subText"
                                  >
                                    {t(
                                      "PRIVACY_MYDATA_REQ_DETAIL_BODY_DATE_SUBMITTED"
                                    )}
                                  </Typography>
                                </Col>
                                <Col md={6}>
                                  <Typography variant="body2" className="value">
                                    {UtilsDateTime.convertDateString(
                                      details.user_request_date
                                    )}
                                  </Typography>
                                </Col>
                                <div className="grid-spacer"></div>
                              </>
                            ) : details.request_status ===
                              RequestStatus.EXPIRED ? (
                              UtilsDateTime.getExpiredDate(details) ? (
                                <>
                                  <Col md={6}>
                                    <Typography
                                      variant="body2"
                                      className="title subText"
                                    >
                                      {t(
                                        "PRIVACY_MYDATA_REQ_DETAIL_BODY_EXP_DATE"
                                      )}
                                    </Typography>
                                  </Col>
                                  <Col md={6}>
                                    <Typography
                                      variant="body2"
                                      className="value"
                                    >
                                      {UtilsDateTime.convertDateString(
                                        details.user_request_date
                                      )}
                                    </Typography>
                                  </Col>
                                  <div className="grid-spacer"></div>
                                </>
                              ) : (
                                <>
                                  <Col md={6}>
                                    <Typography
                                      variant="body2"
                                      className="title subText"
                                    >
                                      {t(
                                        "PRIVACY_MYDATA_REQ_DETAIL_BODY_DATE_SUBMITTED"
                                      )}
                                    </Typography>
                                  </Col>
                                  <Col md={6}>
                                    <Typography
                                      variant="body2"
                                      className="value"
                                    >
                                      {UtilsDateTime.convertDateString(
                                        details.user_request_date
                                      )}
                                    </Typography>
                                  </Col>
                                  <div className="grid-spacer"></div>
                                </>
                              )
                            ) : (
                              details.request_status ===
                                RequestStatus.CANCELED && (
                                <>
                                  <Col md={6}>
                                    <Typography
                                      variant="body2"
                                      className="title subText"
                                    >
                                      {t(
                                        "PRIVACY_MYDATA_REQ_DETAIL_BODY_CANCEL_DATE"
                                      )}
                                    </Typography>
                                  </Col>
                                  <Col md={6}>
                                    <Typography
                                      variant="body2"
                                      className="value"
                                    >
                                      {details.complete_date &&
                                        UtilsDateTime.convertDateString(
                                          details.complete_date
                                        )}
                                    </Typography>
                                  </Col>
                                  <div
                                    data-testid="cancel-req-modal"
                                    className="grid-spacer"
                                  ></div>
                                </>
                              )
                            )}
                            <Col md={6} sm={10}>
                              <Typography
                                variant="body2"
                                className="title subText"
                              >
                                {t("PRIVACY_MYDATA_BODY_SERVICES")}
                              </Typography>
                            </Col>
                            <Col md={6} sm={2}>
                              <Typography variant="body2" className="value">
                                {details.sub_request_count}
                              </Typography>
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <hr />
                            </Col>
                          </Row>
                          <Row className="services">
                            {details.sub_request_list.map((request) => {
                              return (
                                <Col
                                  md={6}
                                  sm={12}
                                  key={request.sub_request_number}
                                >
                                  <div className="request-details__services-list__service">
                                    <Image
                                      image={request.service_icon}
                                      width={40}
                                      height={40}
                                    />
                                    <Typography
                                      variant="body2"
                                      className="service-name"
                                    >
                                      {request.service_name}
                                    </Typography>
                                  </div>
                                </Col>
                              );
                            })}
                          </Row>
                        </Container>
                      </Container>
                    )}
                  </div>
                </Modal.Body>
              )}

              <Modal.Footer>
                <Button
                  variant="solid"
                  type="primary"
                  testId="request-detail-hide-button"
                  title={t("PRIVACY_CANCEL_REQUEST_BTN_CLOSE")}
                  className="btn-close"
                  onClick={handleOnClickHideModal}
                  id="close-details-modal"
                />
              </Modal.Footer>
            </>
          )}
        </Modal>
      </FocusTrapping>
    </>
  );
};

interface CompletedServicesProps {
  details: RequestDetailsEntity;
}

export const DownloadCompletedServices: React.FC<CompletedServicesProps> = (
  props
) => {
  const { t } = useTranslation();
  const [hasDownloadableFile, setHasDownloadableFile] = useState(false);

  const { details } = props;

  useEffect(() => {
    if (UtilsDateTime.getExpiredDate(details)) {
      setHasDownloadableFile(true);
    }
  }, [details]);

  return (
    <Container>
      <Container>
        <Row>
          {UtilsDateTime.getExpiredDate(details) ? (
            <>
              <Col md={6}>
                <Typography variant="body2" className="title subText">
                  {t("PRIVACY_MYDATA_REQ_DETAIL_BODY_EXP_DATE")}
                </Typography>
              </Col>
              <Col md={6}>
                <Typography variant="body2" className="value">
                  {UtilsDateTime.convertDateString(
                    UtilsDateTime.getExpiredDate(details)
                  )}
                </Typography>
              </Col>
              <div className="grid-spacer"></div>
            </>
          ) : (
            <>
              <Col md={6}>
                <Typography variant="body2" className="title subText">
                  {t("PRIVACY_MYDATA_REQ_DETAIL_BODY_DATE_SUBMITTED")}
                </Typography>
              </Col>
              <Col md={6}>
                <Typography variant="body2" className="value">
                  {UtilsDateTime.convertDateString(
                    details.user_request_date || Date.toString()
                  )}
                </Typography>
              </Col>
              <div className="grid-spacer"></div>
            </>
          )}
          <Col md={6} sm={10}>
            <Typography variant="body2" className="title subText">
              {t("PRIVACY_MYDATA_BODY_SERVICES")}
            </Typography>
          </Col>
          <Col md={6} sm={2}>
            <Typography variant="body2" className="value">
              {details.sub_request_count}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col>
            <hr />
          </Col>
        </Row>
        <Row className="services">
          {details.sub_request_list.map((request) => {
            return (
              <Fragment key={request.service_code}>
                <Col md={6} sm={12}>
                  <div className="request-details__services-list__service">
                    <Image
                      image={request.service_icon}
                      width={40}
                      height={40}
                    />
                    <Typography
                      variant="body2"
                      className="service-name subText"
                    >
                      {request.service_name}
                    </Typography>
                  </div>
                </Col>
                {request.attach_file?.length === 0 && hasDownloadableFile ? (
                  <Col md={6} sm={12} className="no-data">
                    <div className="request-details__services-list__files">
                      <Typography variant="body3" weight="w400">
                        {t("PRIVACY_MYDATA_REQ_DETAIL_BODY_EXP_EMPTY")}
                      </Typography>
                    </div>
                  </Col>
                ) : (
                  <Col md={6} sm={12}>
                    {request.attach_file?.map((file, index) => (
                      <div
                        className="request-details__services-list__files"
                        key={file.file_name}
                      >
                        <div className="file-label">
                          <Typography variant="body2" weight="w700">
                            File {("0" + (index + 1)).slice(-2)}
                          </Typography>
                        </div>
                        <div className="file-size">
                          <Typography
                            variant="body2"
                            weight="w400"
                            className="subText"
                          >
                            {UtilsConverter.convertSize(file.file_size)}
                          </Typography>
                        </div>
                        <div className="download-icon">
                          <a
                            download
                            data-testid="trigger-download"
                            href={file.url}
                            onClick={() =>
                              GoogleAnalytics.triggerEvent(
                                EVENT_CATEGORY_REQUEST_DETAILS,
                                EVENT_ACTION_DETAILS_DOWNLOAD_FILE
                              )
                            }
                          >
                            <Svg
                              icon={SVG_ICONS.MYDATA_DOWNLOAD_BTN}
                              width={24}
                              height={24}
                              title={t("PRIVACY_MYDATA_SUMMARY_DOWNLOAD")}
                            />
                          </a>
                        </div>
                      </div>
                    ))}
                  </Col>
                )}
              </Fragment>
            );
          })}
        </Row>
      </Container>
    </Container>
  );
};
